import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { unlockAdminSession } from '@/modules/admin/application/adminSession';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { resolveStructureCode } from '@/modules/profiles/domain/entities/AccessRole';
import { BackButton } from '@/presentation/components/BackButton';
import { Avatar } from '@/presentation/components/Avatar';
import type { AttendanceAction } from '../../domain/entities/Timelog';
import type { AttendanceEmployee } from '../../infrastructure/mappers/timelogMapper';
import { useRegisterTimelog } from '../hooks/useRegisterTimelog';
import { useKeypadInput } from '../hooks/useKeypadInput';
import { useGeolocation } from '@/shared/hooks/useGeolocation';
import { useResolvedAddress } from '@/shared/hooks/useResolvedAddress';
import { VerifyPinUseCase } from '../../application/use-cases/VerifyPinUseCase';
import { getEmployeeLookupRepository } from '../../infrastructure/repositories/container';
// OLD: getTimelogRepository no longer needed (automatic mode now uses accessType = 0)
import { OnlineTimelogError } from '../../infrastructure/repositories/CompositeTimelogRepository';
import { isDefinitelyOnline } from '@/shared/onlineState';
import { CLOCK_WEB_MODE, TIMELOG_SOURCE } from '../../domain/constants/verificationMode';
import { useQualitySettings } from '@/shared/hooks/useQualitySettings';

const PIN_KEYS = ['1','2','3','4','5','6','7','8','9','','0','⌫'];

function accentColor(action: AttendanceAction, C: { success: string; danger: string; teal: string; blue: string }): string {
  if (action === 'entrada')         return C.blue; // was C.success — unified to C.blue (Slice B)
  if (action === 'salida')          return C.blue; // was C.danger  — unified to C.blue (Slice B)
  if (action === 'automatic')       return C.blue; // system decides — same unified blue
  if (action === 'acceso-empleado') return C.blue; // was C.teal    — unified to C.blue (Slice B)
  return C.blue;
}

export function PinPage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { action = 'entrada' } = useParams<{ action: string }>();
  const { selectedRole } = useProfile();
  const typedAction = action as AttendanceAction;
  const accent = accentColor(typedAction, C);
  const employee = (location.state as { employee?: AttendanceEmployee } | null)?.employee ?? null;

  const { coordinates } = useGeolocation();
  const { address } = useResolvedAddress(coordinates);
  const { consecutiveTimelogMinSeconds } = useQualitySettings();
  const [pin, setPin] = useState('');
  const [err, setErr] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const registerTimelog = useRegisterTimelog();

  const isAdmin = typedAction === 'acceso-admin';

  // Use case instantiated once per mount — depends on IEmployeeLookupRepository (port)
  const verifyPinRef = useRef<VerifyPinUseCase | null>(null);
  if (verifyPinRef.current === null) {
    verifyPinRef.current = new VerifyPinUseCase(getEmployeeLookupRepository());
  }
  const verifyPin = verifyPinRef.current;

  const push = useCallback((d: string) => { setErr(false); setPin((p) => p.length < 4 ? p + d : p); }, []);
  const del = useCallback(() => { setErr(false); setPin((p) => p.slice(0, -1)); }, []);

  useKeypadInput({ onDigit: push, onDelete: del, enabled: !registerTimelog.isPending });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (pin.length === 4) {
      void handlePinComplete();
    }
  }, [pin]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handlePinComplete = async () => {
    if (!selectedRole || !employee) {
      setErr(true);
      setTimeout(() => { setPin(''); }, 700);
      return;
    }

    // Always verify PIN against fresh local store (never stale RQ cache)
    const result = await verifyPin.execute({
      accessRoleId: selectedRole.idAccessRole,
      code:         employee.code,
      pin,
    });

    if (!result.ok) {
      setErr(true);
      setAttempts((a) => a + 1);
      setTimeout(() => { setPin(''); }, 700);
      return;
    }

    // Use fresh employee from the use case result for downstream operations
    const verifiedEmployee = result.employee ?? employee;

    // Admin gate: PIN verified — unlock session
    if (isAdmin) {
      unlockAdminSession();
      void navigate('/admin', { replace: true });
      return;
    }

    // Acceso Empleado: no timelog — just open the employee portal after PIN
    // verification. Registering a timelog here would create a phantom entry
    // every time an employee opens their own profile via PIN.
    if (typedAction === 'acceso-empleado') {
      void navigate(`/empleado/${verifiedEmployee.idPerson}`, {
        state: { employee: verifiedEmployee },
        replace: true,
      });
      return;
    }

    // entrada / salida / automatic — register timelog.
    // NEW: Use accessType = 0 for automatic mode (backend decides entrada/salida)
    // OLD: Client-side logic looked up last timelog and toggled 1→2 or 2→1
    let accessType: 0 | 1 | 2 = typedAction === 'salida' ? 2 : typedAction === 'entrada' ? 1 : 0;
    let resolvedAction: AttendanceAction = 
      accessType === 2 ? 'salida' : 
      accessType === 1 ? 'entrada' : 
      'automatic';
    
    // OLD automatic logic (commented for rollback):
    // let accessType: 1 | 2 = typedAction === 'salida' ? 2 : 1;
    // if (typedAction === 'automatic') {
    //   try {
    //     const repo = getTimelogRepository();
    //     const last = await repo.getLastByPerson(verifiedEmployee.idPerson);
    //     accessType = last?.accessType === 1 ? 2 : 1;
    //   } catch (err) {
    //     console.warn('[PinPage] Automatic lookup failed — defaulting to entrada:', err);
    //     accessType = 1;
    //   }
    // }
    // const resolvedAction: AttendanceAction = accessType === 2 ? 'salida' : 'entrada';

    const idStructure = selectedRole.structures[0] ?? 0;

    try {
      await registerTimelog.execute({
        employee: {
          idEntity:    verifiedEmployee.idEntity,
          idBiometric: verifiedEmployee.pinBiometricId ?? null,
        },
        idStructure,
        structureCode:   resolveStructureCode(selectedRole, idStructure),
        accessType,
        verificationMode: CLOCK_WEB_MODE.pin,
        isOffline:       !isDefinitelyOnline(),
        coordinates:     coordinates ?? { lat: 0, lng: 0 },
        location:        address,
        source:          TIMELOG_SOURCE.clockWeb,
        consecutiveMinSeconds: consecutiveTimelogMinSeconds,
      });

      void navigate('/success', { state: { employee: verifiedEmployee, action: resolvedAction }, replace: true });
    } catch (err) {
      // Backend rejection (online) or consecutive guard (offline) — route to
      // /error with the specific code so the user sees a real error page
      // instead of the "PIN incorrecto" inline flow. `code` + `elapsedSeconds`
      // let MarkingErrorPage pick specific copy.
      if (err instanceof OnlineTimelogError) {
        void navigate('/error', {
          state: { action: resolvedAction, method: 'pin', errorCode: err.code, elapsedSeconds: err.elapsedSeconds, minSeconds: err.minSeconds },
          replace: true,
        });
        return;
      }
      setErr(true);
      setAttempts((a) => a + 1);
      setTimeout(() => { setPin(''); }, 700);
    }
  };

  const backPath = isAdmin ? `/id-input/acceso-admin` : `/id-input/${action}`;
  const title = isAdmin ? t('pin.titleAdmin') : typedAction === 'acceso-empleado' ? t('pin.titleEmployee') : typedAction === 'entrada' ? t('pin.titleEntry') : t('pin.titleExit');
  const label = isAdmin ? t('pin.labelAdmin') : typedAction === 'entrada' ? t('pin.labelEntry') : typedAction === 'salida' ? t('pin.labelExit') : t('pin.labelAccess');

  return (
    <div style={{ flex: 1, background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px 30px 26px' }}>
      <div className="mi-anim-fade-in" style={{ flex: 1, width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Header */}
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', marginBottom: 18, position: 'relative' }}>
          <BackButton T={T} onClick={() => { void navigate(backPath); }} />
          <div style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: 14, fontWeight: 700, color: T.text, pointerEvents: 'none' }}>{title}</div>
          <div style={{ marginLeft: 'auto', padding: '5px 13px', borderRadius: 15, fontSize: 9, fontWeight: 700, letterSpacing: '.06em', background: `${accent}18`, color: accent, zIndex: 1 }}>
            ● {label}
          </div>
        </div>

        {/* Employee pill or lock icon */}
        {employee ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 16px 9px 9px', borderRadius: 30, border: `1px solid ${T.border}`, background: T.surfaceBg, marginBottom: 14 }}>
            <Avatar initials={employee.initials} color={employee.avatarColor} size={34} photoUrl={employee.photoUrl ?? null} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, lineHeight: 1.2 }}>{employee.fullName}</div>
              <div style={{ fontSize: 9, color: T.textMuted, marginTop: 2 }}>{employee.code}</div>
            </div>
          </div>
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
            <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
        )}

        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>
          {isAdmin ? t('pin.headingAdmin') : t('pin.headingGreeting', { name: employee?.fullName.split(' ')[0] ?? '' })}
        </div>
        <div style={{ fontSize: 10, color: T.textMuted, textAlign: 'center', marginBottom: 18, maxWidth: 270, lineHeight: 1.6 }}>
          {isAdmin
            ? t('pin.hintAdmin')
            : typedAction === 'salida'
              ? t('pin.hintExit')
              : typedAction === 'acceso-empleado'
                ? t('pin.hintAccess')
                : t('pin.hintEntry')}
        </div>

        {/* PIN dots */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={err ? 'mi-anim-shake' : ''}
              style={{
                width: 36, height: 42, borderRadius: 8,
                border: `2px solid ${err ? C.danger : i < pin.length ? accent : T.border}`,
                background: i < pin.length ? (err ? C.danger : accent) + '18' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, fontWeight: 700, color: err ? C.danger : accent,
                transition: 'all .2s',
              }}
            >
              {i < pin.length ? '●' : ''}
            </div>
          ))}
        </div>

        <div style={{ minHeight: 16, marginBottom: 12 }}>
          {err && <div style={{ fontSize: 10, color: C.danger, fontWeight: 600 }}>{attempts > 1 ? t('pin.errorAttempt', { count: attempts }) : t('pin.errorWrong')}</div>}
        </div>

        {/* Keypad */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, width: 240 }}>
          {PIN_KEYS.map((d, i) => (
            <button
              key={i}
              onClick={() => { if (d === '⌫') del(); else if (d) push(d); }}
              disabled={!d || registerTimelog.isPending}
              style={{
                height: 50, borderRadius: 12, border: `1px solid ${T.border}`,
                background: d ? T.surfaceBg : 'transparent',
                fontFamily: 'inherit', fontSize: 17, fontWeight: 700, color: T.text,
                cursor: d && !registerTimelog.isPending ? 'pointer' : 'default',
                transition: 'all .15s',
                visibility: d ? 'visible' : 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
              onMouseEnter={(e) => { if (d) e.currentTarget.style.background = T.rowBg; }}
              onMouseLeave={(e) => { if (d) e.currentTarget.style.background = T.surfaceBg; }}
            >
              {d === '⌫' ? (
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M22 3H7l-5 9 5 9h15a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/>
                  <line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/>
                </svg>
              ) : d}
            </button>
          ))}
        </div>

        {/* Alt methods for admin */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 16 }}>
            <button onClick={() => { void navigate('/scan/acceso-admin'); }} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.blue}40`, background: `${C.blue}10`, color: C.blue, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="9" r="3"/><path d="M5 20a7 7 0 0 1 14 0"/><path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0z"/></svg>
              {t('methods.useFace')}
            </button>
            <button onClick={() => { void navigate('/card-scan/acceso-admin'); }} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.blue}40`, background: `${C.blue}10`, color: C.blue, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="13" rx="2"/><path d="M16 12h.01M13 12h.01"/></svg>
              {t('methods.useCard')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
